import { CloudUploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Card, Spin } from "antd";
import { useState } from "react";
import Alert from "react-bootstrap/Alert";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import FileUpload from "../../../components/FileUpload";
import api from "../../../services/api";

interface ResponseError {
  message: string;
}

interface ErrorItem {
  row?: number;
  error: string;
  data?: any;
}

interface ResponseUpload {
  success: boolean;
  message: string;
  totalRecords: number;
  importedRecords: number;
  errors?: ErrorItem[];
}

type ResponseAPI = ResponseUpload | ResponseError;

function ErrorList({ errors }: { errors: ErrorItem[] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  if (!errors || errors.length === 0) {
    return null;
  }

  const displayErrors = showAll ? errors : errors.slice(0, 5);
  const hasMoreErrors = errors.length > 5;

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      setShowAll(false);
    }
  };

  const formatError = (errorItem: ErrorItem, index: number) => {
    const rowInfo = errorItem.row ? `Linha ${errorItem.row}: ` : "";
    return `${rowInfo}${errorItem.error}`;
  };

  return (
    <div className="error-list-container">
      <div className="error-header">
        <strong>Erros encontrados ({errors.length}):</strong>
        <button
          type="button"
          className="expand-btn"
          onClick={handleExpandToggle}
        >
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          {isExpanded ? " Ocultar" : " Ver detalhes"}
        </button>
      </div>

      {isExpanded && (
        <div className="error-details">
          <ul className="error-list">
            {displayErrors.map((errorItem, index) => (
              <li key={index} className="error-item">
                <span className="error-number">{index + 1}.</span>
                <span className="error-text">
                  {formatError(errorItem, index)}
                </span>
              </li>
            ))}
          </ul>

          {hasMoreErrors && !showAll && (
            <button
              type="button"
              className="show-more-btn"
              onClick={() => setShowAll(true)}
            >
              Ver mais {errors.length - 5} erros...
            </button>
          )}

          {hasMoreErrors && showAll && (
            <button
              type="button"
              className="show-less-btn"
              onClick={() => setShowAll(false)}
            >
              Ver menos
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function returnTableorNot(
  response: ResponseAPI | null,
  searchClicked: boolean
) {
  if (!response) {
    return null;
  }

  if ("success" in response) {
    const uploadResponse = response as ResponseUpload;
    const hasErrors = uploadResponse.errors && uploadResponse.errors.length > 0;

    return (
      <Alert
        key={"success"}
        variant={hasErrors ? "warning" : "success"}
        className="mt-3"
      >
        <strong>{uploadResponse.message}</strong>
        <br />
        Total de registros: {uploadResponse.totalRecords} | Importados:{" "}
        {uploadResponse.importedRecords}
        {hasErrors && (
          <div style={{ marginTop: "1rem" }}>
            <ErrorList errors={uploadResponse.errors || []} />
          </div>
        )}
      </Alert>
    );
  }

  if ("message" in response && searchClicked) {
    return (
      <Alert key={"danger"} variant={"danger"} className="mt-3">
        Erro no upload: {(response as ResponseError).message}
      </Alert>
    );
  }

  return null;
}

function refreshPage() {
  window.location.href = "/upload-contratos";
}

export const UploadContratosTab = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [responseUpload, setResponseUpload] = useState<ResponseAPI | null>(
    null
  );
  const [searchClicked, setSearchClicked] = useState<boolean>(false);

  const handleFileSelect = (selectedFile: File | null) => {
    setFile(selectedFile);
    if (selectedFile) {
      setResponseUpload(null);
      setSearchClicked(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      alert("Por favor, selecione um arquivo antes de enviar.");
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    setLoading(true);
    setSearchClicked(true);
    setResponseUpload(null);

    reader.onloadend = async () => {
      try {
        await api
          .post("/frete-maritmo/upload", {
            data: reader.result,
          })
          .then((res) => {
            setResponseUpload(res.data);
          });
      } catch (error) {
        console.error("Erro ao enviar arquivo:", error);
        setResponseUpload({
          message:
            "Erro ao processar o arquivo. Verifique se o formato está correto.",
        });
      } finally {
        setLoading(false);
      }
    };
  };

  return (
    <div className="p-3">
      <div className="main-content-title">
        <span className="flex items-center gap-2">
          <CloudUploadOutlined style={{ fontSize: 35 }} />
          <h3 className="text-2xl ">Freight contract</h3>
        </span>
        <p className="mt-2 flex items-center gap-3">
          Faça download do modelo de contratos e importe os dados no sistema
          <a
            className="flex items-center gap-1"
            href="https://zarpar-public.s3.us-east-2.amazonaws.com/public/FreightContractTemplate.xlsx"
            target="_blank"
          >
            <DownloadOutlined />
            Download modelo
          </a>
        </p>

      </div>
      <div className="w-full">
        <div className="flex justify-center">
          <div className="Upload-form">
            <FileUpload
              accept=".xlsx"
              onFileSelect={handleFileSelect}
              placeholder="Selecione um arquivo Excel (.xlsx)"
              maxSize={10}
              multiple={false}
              disabled={loading}
            />

            {loading && (
              <div className="upload-loading">
                <Spin
                  size="large"
                  tip="Processando arquivo... Aguarde enquanto importamos os dados."
                />
                <p className="loading-note">
                  Este processo pode levar alguns minutos dependendo do tamanho
                  do arquivo.
                </p>
              </div>
            )}

            <div className="upload-actions">
              <button
                disabled={loading || !file}
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                {loading ? "Processando..." : "Enviar"}
              </button>
              <button
                className="btn btn-secondary"
                onClick={refreshPage}
                disabled={loading}
              >
                Limpar
              </button>
            </div>
          </div>
        </div>
      </div>
      {returnTableorNot(responseUpload, searchClicked)}
    </div>
  );
};
